import { writable, get } from 'svelte/store'
import { getUnifiedCache } from '$lib/services/UnifiedCache'
import { contentIndexStore } from './contentIndexStore'

interface VaultState {
  isInitialized: boolean
  vaultHandle: FileSystemDirectoryHandle | null
  vaultName: string
  vaultPath: string
}

// Extract folder name from path
function getFolderName(path: string): string {
  // Remove trailing slash if present
  const cleanPath = path.replace(/[/\\]+$/, '')
  // Get the last segment
  const segments = cleanPath.split(/[/\\]/)
  return segments[segments.length - 1] || path
}

function createVaultStore() {
  // Fire-and-forget content index initialization — never blocks vault setup
  function scheduleContentIndexInit(vaultPath: string): void {
    (async () => {
      try {
        await contentIndexStore.initialize(vaultPath)
        const state = contentIndexStore.getState()
        const totalDocs = state?.stats?.totalDocuments || 0
        if (totalDocs === 0) {
          contentIndexStore.performFullIndexing().catch(err => {
            console.error('Background indexing failed:', err)
          })
        } else {
          const isValid = await contentIndexStore.validateCache()
          if (!isValid) {
            contentIndexStore.performFullIndexing().catch(err => {
              console.error('Background indexing failed:', err)
            })
          }
        }
      } catch (err) {
        console.error('Failed to initialize Content Index:', err)
      }
    })()
  }

  // Try to restore vault path from localStorage for web environments
  const savedVaultName = typeof window !== 'undefined' ?
    localStorage.getItem('korean-grammar-current-vault') : ''

  const { subscribe, set, update } = writable<VaultState>({
    isInitialized: false,
    vaultHandle: null,
    vaultName: savedVaultName || '',
    vaultPath: savedVaultName || ''  // Use saved name as path for web
  })

  // Restore vault path in Electron on page refresh
  if (typeof window !== 'undefined' && (window as any).api?.getVaultPath) {
    ;(async () => {
      try {
        const result = await (window as any).api.getVaultPath()
        if (result.success && result.path) {
          const folderName = getFolderName(result.path)
          set({
            isInitialized: true,
            vaultHandle: { path: result.path, name: folderName } as any,
            vaultName: folderName,
            vaultPath: result.path
          })

          // Schedule content index initialization — fire-and-forget
          scheduleContentIndexInit(result.path)
        }
      } catch (err) {
        console.debug('Could not restore vault path on refresh:', err)
      }
    })()
  }
  
  return {
    subscribe,
    
    async setVault(dirHandle: FileSystemDirectoryHandle, options?: { skipIndexing?: boolean }) {
      // Check if in demo mode
      const urlParams = new URLSearchParams(window.location.search);
      const isDemoMode = urlParams.get('demo') === 'true';

      // Get the current vault path to check if we're switching vaults
      const state = get(this)
      const currentVaultPath = state.vaultPath
      const newVaultPath = (dirHandle as any).path || dirHandle.name
      
      // Only cleanup if we're actually changing vaults
      if (currentVaultPath && currentVaultPath !== newVaultPath) {
        // Clean up previous content index when switching vaults
        await contentIndexStore.cleanup()
      }
      
      // For web, also check saved vault name in localStorage since vaultPath might not be initialized yet
      const savedVaultName = typeof window !== 'undefined' ? 
        localStorage.getItem('korean-grammar-current-vault') : null
      
      // Check if we're initializing (no current vault) or switching vaults
      // Consider it the same vault if the saved name matches the new path
      const isInitializing = !currentVaultPath && !state.isInitialized
      const isSameVault = savedVaultName && (savedVaultName === newVaultPath || savedVaultName === dirHandle.name)
      const isSwitchingVault = currentVaultPath && currentVaultPath !== newVaultPath && !isSameVault
      
      
      // 이미 같은 vault가 초기화되어 있으면 중복 초기화 방지
      if (state.isInitialized && currentVaultPath === newVaultPath) {
        return
      }
      
      // Only close tabs and clear persistence when switching to a different vault
      // Keep them when initializing the same vault on app restart
      if (isSwitchingVault) {
        // Clear all existing tabs before setting new vault
        const { fileStore } = await import('./fileStore')
        await fileStore.closeAllTabs()
        
        // Clear tab persistence
        localStorage.removeItem('korean-grammar-open-tabs')
        localStorage.removeItem('korean-grammar-active-tab')
      } else if (isSameVault || isInitializing) {
        // Don't close tabs or clear persistence - they will be restored after vault init
      } else {
        // Same vault re-selected, keeping everything
      }
      
      // Check if we're in Electron and use IPC to select vault
      const isElectron = typeof window !== 'undefined' && (window as any).api
      
      if (isElectron) {
        // In Electron, the directory handle comes from selectVault dialog
        // Check if the handle has a path property (from Electron)
        const actualPath = (dirHandle as any).path || dirHandle.name
        const folderName = getFolderName(actualPath)
        
        // Save vault info to localStorage for restoration
        if (typeof window !== 'undefined') {
          localStorage.setItem('selectedVault', JSON.stringify({
            path: actualPath,
            name: folderName,
            isElectron: true
          }))
        }
        
        set({
          isInitialized: true,
          vaultHandle: dirHandle,
          vaultName: folderName,  // Use only the folder name
          vaultPath: actualPath  // Use the actual full path
        })
        
        // Initialize cache in system directory (no migration needed for new setup)
        // Cache will be automatically created in the correct location by the backend
        
        // Schedule content index initialization — fire-and-forget
        scheduleContentIndexInit(actualPath)
        
        // Initialize metadata cache only in renderer process (not Electron main)
        // SQLite cache is handled by IPC in Electron
        if (!isElectron || !(window as any).api?.fullScan) {
          // Browser environment - use UnifiedCache (IndexedDB backend)
          try {
            const cache = await getUnifiedCache()
            await cache.init(actualPath)
            
            // Check if we have a valid cache first
            const stats = await cache.getStats()
            const now = Date.now()
            const cacheAge = stats?.lastFullScan ? now - stats.lastFullScan : Infinity
            const cacheValid = cacheAge < 30 * 60 * 1000 // 30 minutes
            
            if (cacheValid) {
              const nodes = await cache.getFullTree()
            } else {
              // Only perform full scan if cache is stale or missing
              // Cache is stale or missing, performing full scan
              const nodes = await this.fullMetadataScanWithCache()
            }
          } catch (err) {
            console.error('Failed to initialize metadata cache:', err)
          }
        } else {
          // Electron environment - SQLite cache is handled by main process
          // IPC handler will check cache validity, no need to scan here
        }
        
        // Save vault reference for persistence
        localStorage.setItem('korean-grammar-current-vault', actualPath)
        localStorage.setItem('korean-grammar-vault-path', actualPath)
        return
      }
      
      // Request write permission immediately when vault is selected (for web)
      if (!isElectron) {
        try {
          if ('requestPermission' in dirHandle && typeof dirHandle.requestPermission === 'function') {
            const permission = await dirHandle.requestPermission({ mode: 'readwrite' })
            if (permission !== 'granted') {
              console.warn('Write permission not granted for vault')
            }
          }
        } catch (err) {
          console.error('Failed to request permission:', err)
        }
      }
      
      // Save vault info to localStorage for web restoration
      if (typeof window !== 'undefined') {
        localStorage.setItem('selectedVault', JSON.stringify({
          name: dirHandle.name,
          isElectron: false
        }))
      }
      
      set({
        isInitialized: true,
        vaultHandle: dirHandle,
        vaultName: dirHandle.name,
        vaultPath: dirHandle.name
      })
      
      // Schedule content index initialization — fire-and-forget (skip in demo mode)
      if (!isDemoMode && !options?.skipIndexing) {
        scheduleContentIndexInit(dirHandle.name)
      }
      
      // Save vault reference for persistence
      localStorage.setItem('korean-grammar-current-vault', dirHandle.name)
    },
    
    async clearVault() {
      // Clean up content index
      await contentIndexStore.cleanup()
      
      // Clear all existing tabs when clearing vault
      const { fileStore } = await import('./fileStore')
      await fileStore.closeAllTabs()
      
      set({
        isInitialized: false,
        vaultHandle: null,
        vaultName: '',
        vaultPath: ''
      })
      
      // Clear vault-related localStorage items (but not tabs - they should persist)
      localStorage.removeItem('korean-grammar-current-vault')
      localStorage.removeItem('korean-grammar-vault-path')
      // Don't clear tabs here - they will be cleared when a new vault is opened
      // or when explicitly closing all tabs
    },
    
    async fileExists(path: string): Promise<boolean> {
      try {
        await this.readFile(path, true) // silent mode
        return true
      } catch {
        return false
      }
    },
    
    async readFile(path: string, silent: boolean = false, force: boolean = false): Promise<string> {
      const state = get({ subscribe })
      if (!state.vaultHandle) throw new Error('No vault selected')
      
      // Check if we're in Electron and use IPC
      const isElectron = typeof window !== 'undefined' && (window as any).api
      
      if (isElectron) {
        // Use Electron IPC for file reading
        try {
          const vaultPath = state.vaultPath || ''
          // If path is already absolute, use it as-is
          // If path starts with vault path, use it as-is
          // Otherwise, combine vault path with relative path
          let fullPath = path
          if (!path.startsWith('/') && !path.startsWith(vaultPath)) {
            fullPath = `${vaultPath}/${path}`
          }
          const result = await (window as any).api.readFile(fullPath, force) // Pass force parameter
          if (result.success) {
            return result.content || ''
          } else {
            throw new Error(result.error || 'Failed to read file')
          }
        } catch (err) {
          if (!silent) {
            console.error('Failed to read file via IPC:', path, err)
          }
          throw err
        }
      }
      
      // Use File System Access API for web
      try {
        // Check and request permission if needed (for any read operation, not just force)
        if (state.vaultHandle && 'queryPermission' in state.vaultHandle) {
          try {
            const permission = await state.vaultHandle.queryPermission?.({ mode: 'read' })
            if (permission !== 'granted' && 'requestPermission' in state.vaultHandle) {
              console.log('[vaultStore] Requesting read permission...')
              const newPermission = await state.vaultHandle.requestPermission?.({ mode: 'read' })
              if (newPermission !== 'granted') {
                throw new Error('Permission denied for file access')
              }
              console.log('[vaultStore] Read permission granted')
            }
          } catch (permErr) {
            // Permission API might not be supported, continue with file access attempt
            console.debug('Permission check failed, attempting direct read:', permErr)
          }
        }
        
        // Check if path contains folders
        const parts = path.split('/')
        let currentHandle = state.vaultHandle
        
        // Try to get the file handle with retries for stale handles and permission errors
        let fileHandle: FileSystemFileHandle | null = null
        let retryCount = 0
        const maxRetries = force ? 3 : 2  // More retries when forcing
        let permissionRequested = false

        while (retryCount <= maxRetries && !fileHandle) {
          try {
            if (parts.length > 1) {
              // Reset to root handle for retry
              currentHandle = state.vaultHandle

              // Navigate through folders
              for (let i = 0; i < parts.length - 1; i++) {
                currentHandle = await currentHandle.getDirectoryHandle(parts[i])
              }
              // Get the file from the final folder
              const filename = parts[parts.length - 1]
              fileHandle = await currentHandle.getFileHandle(filename)
            } else {
              // File in root directory
              fileHandle = await state.vaultHandle.getFileHandle(path)
            }

            // If force reload, verify we have fresh handle by getting a new one
            if (force && retryCount === 0) {
              // Force getting a fresh file handle
              const filename = parts[parts.length - 1] || path
              fileHandle = await currentHandle.getFileHandle(filename)
            }
          } catch (handleErr) {
            if (handleErr instanceof DOMException && handleErr.name === 'NotFoundError') {
              // File genuinely doesn't exist, throw immediately
              throw handleErr
            }

            // If permission error and haven't requested yet, try to request permission
            if (handleErr instanceof DOMException &&
                handleErr.name === 'NotAllowedError' &&
                !permissionRequested &&
                state.vaultHandle &&
                'requestPermission' in state.vaultHandle) {
              try {
                console.log('[vaultStore] Permission error, requesting read permission...')
                const permission = await state.vaultHandle.requestPermission?.({ mode: 'read' })
                if (permission === 'granted') {
                  console.log('[vaultStore] Read permission granted, retrying...')
                  permissionRequested = true
                  // Don't increment retry count for permission request
                  continue
                }
              } catch (permErr) {
                console.error('[vaultStore] Failed to request permission:', permErr)
              }
            }

            // For other errors (like stale handle), retry
            retryCount++
            if (retryCount > maxRetries) {
              throw handleErr
            }

            // Wait a bit before retry
            await new Promise(resolve => setTimeout(resolve, 100))
          }
        }
        
        if (!fileHandle) {
          throw new Error('Failed to get file handle after retries')
        }
        
        // Read the file - force getting a fresh File object
        const file = await fileHandle.getFile()
        
        // If force is true and file seems stale, try once more
        if (force) {
          // Small delay to ensure file system has updated
          await new Promise(resolve => setTimeout(resolve, 50))
          // Get file again to ensure we have the latest version
          const freshFile = await fileHandle.getFile()
          const content = await freshFile.text()
          console.log(`[vaultStore] Force read file ${path}: ${content.substring(0, 100)}...`)
          return content
        }
        
        return await file.text()
      } catch (err) {
        // In web environment, try to read from IndexedDB cache if file handle fails (unless force is true)
        if (!force && err instanceof DOMException && (err.name === 'NotFoundError' || err.name === 'NotAllowedError')) {
          // This is expected when permissions are lost - don't log as error
          console.debug('File access permission lost, attempting cache recovery:', path)
          // First try to get from contentIndexStore
          try {
            const document = await contentIndexStore.getDocument(path)
            if (document && document.content) {
              console.debug(`Retrieved content from content index for: ${path}`)
              return document.content
            }
          } catch (indexErr) {
            console.debug('Content index fallback failed:', indexErr)
          }
          
          // Try IndexedDB directly with the correct database name
          try {
            // Get vault name from state
            const vaultName = state.vaultName || state.vaultHandle?.name || 'default'
            const dbName = `ContentIndexDB-${vaultName}`
            
            // Open IndexedDB with correct name
            const request = indexedDB.open(dbName, 1)
            const db = await new Promise<IDBDatabase>((resolve, reject) => {
              request.onsuccess = () => resolve(request.result)
              request.onerror = () => reject(request.error)
            })
            
            try {
              const tx = db.transaction(['documents'], 'readonly')
              const store = tx.objectStore('documents')
              
              // Try different key formats
              const pathWithoutExt = path.replace('.md', '')
              const pathWithVault = `${vaultName}/${pathWithoutExt}`.replace('.md', '')
              
              // Try with the path without extension first
              let getRequest = store.get(pathWithoutExt)
              let doc = await new Promise<any>((resolve) => {
                getRequest.onsuccess = () => resolve(getRequest.result)
                getRequest.onerror = () => resolve(null)
              })
              
              // If not found, try with vault prefix
              if (!doc) {
                getRequest = store.get(pathWithVault)
                doc = await new Promise<any>((resolve) => {
                  getRequest.onsuccess = () => resolve(getRequest.result)
                  getRequest.onerror = () => resolve(null)
                })
              }
              
              // If still not found, try the original path
              if (!doc) {
                getRequest = store.get(path)
                doc = await new Promise<any>((resolve) => {
                  getRequest.onsuccess = () => resolve(getRequest.result)
                  getRequest.onerror = () => resolve(null)
                })
              }
              
              if (doc && doc.content) {
                console.debug(`Retrieved content from IndexedDB for: ${path}`)
                return doc.content
              }
            } finally {
              db.close()
            }
          } catch (dbErr) {
            console.debug('IndexedDB fallback also failed:', dbErr)
          }
          
          // If we couldn't get the content from anywhere, return empty content
          // This prevents the "[읽기 실패]" prefix from appearing
          console.debug(`Could not retrieve content for ${path}, returning empty content`)
          return ''
        }
        
        throw err
      }
    },
    
    async writeFile(path: string, content: string): Promise<void> {
      const state = get({ subscribe })
      if (!state.vaultHandle) throw new Error('No vault selected')
      
      // Check if we're in Electron and use IPC
      const isElectron = typeof window !== 'undefined' && (window as any).api
      
      if (isElectron) {
        // Use Electron IPC for file writing
        try {
          const vaultPath = state.vaultPath || ''
          // If path is already absolute, use it as-is
          // If path starts with vault path, use it as-is
          // Otherwise, combine vault path with relative path
          let fullPath = path
          if (!path.startsWith('/') && !path.startsWith(vaultPath)) {
            fullPath = `${vaultPath}/${path}`
          }
          const result = await (window as any).api.writeFile(fullPath, content, true)
          if (!result.success) {
            throw new Error(result.error || 'Failed to write file')
          }
          
          // Immediately update content index after successful write
          if (path.endsWith('.md')) {
            try {
              const { contentIndexStore } = await import('./contentIndexStore')
              // Update the document in the index
              await contentIndexStore.updateSingleDocument(path, content)
            } catch (indexError) {
              console.warn('Failed to update content index:', indexError)
              // Don't throw - file save succeeded even if indexing failed
            }
          }
          
          return
        } catch (err) {
          console.error('Failed to write file via IPC:', path, err)
          throw err
        }
      }
      
      // Use File System Access API for web
      try {
        // Check and request permission if needed
        let hasPermission = false
        try {
          if ('queryPermission' in state.vaultHandle && typeof state.vaultHandle.queryPermission === 'function') {
            const permissionStatus = await state.vaultHandle.queryPermission({ mode: 'readwrite' })
            hasPermission = permissionStatus === 'granted'

            // Request permission if not granted (user gesture available from Tab key, save button, etc.)
            if (!hasPermission && 'requestPermission' in state.vaultHandle && typeof state.vaultHandle.requestPermission === 'function') {
              console.log('[vaultStore] Requesting write permission...')
              const newPermission = await state.vaultHandle.requestPermission({ mode: 'readwrite' })
              hasPermission = newPermission === 'granted'

              if (!hasPermission) {
                throw new Error('파일 저장 권한이 거부되었습니다. Vault 폴더를 다시 선택해주세요.')
              }
              console.log('[vaultStore] Write permission granted')
            }
          }
        } catch (permErr) {
          // If permission request failed or not supported, log and try direct write
          console.warn('Permission request failed, attempting direct write:', permErr)
        }
        
        // Check if path contains folders
        const parts = path.split('/')
        let currentHandle = state.vaultHandle
        
        // Create folders if path contains them
        if (parts.length > 1) {
          // Navigate/create folders
          for (let i = 0; i < parts.length - 1; i++) {
            currentHandle = await currentHandle.getDirectoryHandle(parts[i], { create: true })
          }
          // Create the file in the final folder
          const filename = parts[parts.length - 1]
          const fileHandle = await currentHandle.getFileHandle(filename, { create: true })
          
          // Write the content
          const writable = await fileHandle.createWritable()
          await writable.write(content)
          await writable.close()
        } else {
          // File in root directory
          const fileHandle = await state.vaultHandle.getFileHandle(path, { create: true })
          
          // Write the content
          const writable = await fileHandle.createWritable()
          await writable.write(content)
          await writable.close()
        }
        
        // Immediately update content index after successful write
        if (path.endsWith('.md')) {
          try {
            const { contentIndexStore } = await import('./contentIndexStore')
            // Update the document in the index
            await contentIndexStore.updateSingleDocument(path, content)
          } catch (indexError) {
            console.warn('Failed to update content index:', indexError)
            // Don't throw - file save succeeded even if indexing failed
          }
        }
      } catch (err) {
        console.error('Failed to write file:', path, err)
        // Check if it's a permission error
        if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
          throw new Error('파일 저장 권한이 없습니다. Vault 폴더를 다시 선택하거나, 수동으로 저장(Cmd/Ctrl+S)해주세요.')
        }
        throw err
      }
    },
    
    async createFolder(folderPath: string): Promise<string> {
      const state = get({ subscribe })
      
      // Check if we're in Electron and use IPC
      const isElectron = typeof window !== 'undefined' && (window as any).api
      
      if (isElectron) {
        // Use Electron IPC for folder creation
        try {
          const vaultPath = state.vaultPath || ''
          // Build the full path
          let fullPath = folderPath
          if (!folderPath.startsWith('/') && !folderPath.startsWith(vaultPath)) {
            fullPath = `${vaultPath}/${folderPath}`
          }
          
          // Use the createFolder API (now always available)
          const result = await (window as any).api.createFolder(fullPath)
          
          if (result && result.success !== false) {
            return folderPath
          } else {
            throw new Error(result?.error || 'Failed to create folder')
          }
        } catch (err) {
          console.error('Failed to create folder via IPC:', folderPath, err)
          throw err
        }
      }
      
      if (!state.vaultHandle) throw new Error('No vault selected')
      
      // Web: Use File System Access API
      try {
        // Handle nested folders
        const parts = folderPath.split('/')
        let currentHandle = state.vaultHandle
        
        for (const part of parts) {
          if (part) {
            currentHandle = await currentHandle.getDirectoryHandle(part, { create: true })
          }
        }
        
        return folderPath
      } catch (err) {
        console.error('Failed to create folder:', folderPath, err)
        throw err
      }
    },
    
    async fullMetadataScanWithCache(): Promise<any[]> {
      // Always perform full scan and update cache
      // This method should only be called when cache is invalid
      const nodes = await this.fullMetadataScan()
      
      // Update cache with fresh data (only in browser environment)
      const isElectron = typeof window !== 'undefined' && window.api?.fullScan
      if (!isElectron) {
        const cache = await getUnifiedCache()
        await cache.setFullTree(nodes)
        console.log('Updated cache with fresh metadata')
      }
      // Electron cache update is handled by IPC handler
      
      return nodes
    },
    
    async getCachedTree(): Promise<any[]> {
      // Get tree from cache without scanning
      const isElectron = typeof window !== 'undefined' && window.api?.fullScan
      
      if (isElectron && window.api?.getCachedTree) {
        // Get from SQLite cache via IPC
        const response = await window.api.getCachedTree({ 
          path: get(this).vaultPath || ''
        })
        return response.nodes || []
      } else {
        // Get from cache
        try {
          const cache = getUnifiedCache()
          const state = get(this)
          
          // Initialize cache if needed
          if (!cache.initialized && state.vaultPath) {
            await cache.init(state.vaultPath)
          }
          
          const nodes = await cache.getFullTree()
          return nodes || []
        } catch (err) {
          console.debug('Cache not ready yet:', err)
          return []
        }
      }
    },
    
    async fullMetadataScan(): Promise<any[]> {
      const state = get({ subscribe })
      if (!state.vaultPath) {
        console.error('No vault path set')
        return []
      }
      
      // Check if we're in Electron environment
      const isElectron = typeof window !== 'undefined' && window.api?.fullScan
      
      if (isElectron) {
        try {
          const startTime = performance.now()
          // Reduce logging
          // console.log('🚀 Starting full metadata scan of vault...')
          
          const result = await window.api!.fullScan!(state.vaultPath, {
            extensions: ['.md', '.txt', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.ico'],
            includeHidden: false
          })
          
          if (!result.success) {
            console.error('Full scan failed:', result.error)
            return []
          }
          
          const elapsed = performance.now() - startTime
          // Only log if scan takes more than 100ms
          if (elapsed > 100) {
            console.log(`✨ Full scan completed in ${elapsed.toFixed(0)}ms - ${result.nodes?.length || 0} items`)
          }
          
          // Cache the scan results (already cached in Electron main process)
          // No need to cache here as it's handled by IPC
          
          return result.nodes || []
        } catch (error) {
          console.error('Full scan error:', error)
          return []
        }
      }
      
      // Fallback for non-Electron environment
      console.warn('Full scan not available in non-Electron environment')
      return []
    },
    
    async listFiles(dirPath: string = '', recursive: boolean = false): Promise<{ name: string; path: string; type: 'file' | 'directory'; size?: number; modified?: number }[]> {

      const state = get({ subscribe })

      const entries: { name: string; path: string; type: 'file' | 'directory'; size?: number; modified?: number }[] = []

      // Check if in demo mode
      const urlParams = new URLSearchParams(window.location.search);
      const isDemoMode = urlParams.get('demo') === 'true';
      if (isDemoMode) {
        // Return empty list in demo mode to avoid file system access
        return [];
      }

      // Check if we're in Electron environment
      const isElectron = typeof window !== 'undefined' &&
                         ((window as any).api ||
                          (window as any).electronAPI ||
                          navigator.userAgent.toLowerCase().includes('electron'))
      
      if (isElectron && (window as any).api?.listFiles) {
        // Use Electron IPC for file listing
        try {
          const vaultPath = state.vaultPath || ''
          const fullPath = dirPath ? `${vaultPath}/${dirPath}` : vaultPath
          
          // Include both markdown and image files
          const extensions = ['md', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp']
          const result = await (window as any).api.listFiles(fullPath, extensions)
          
          if (result.success && result.files) {
            // console.log('Electron listFiles - files count:', result.files.length)
            
            // Process the new format with file and directory information
            for (const file of result.files) {
              // When not recursive, filter based on the requested directory
              if (!recursive) {
                if (dirPath) {
                  // When we query a specific folder, the API returns files within that folder
                  // The paths might be:
                  // 1. Relative to the folder (e.g., "Untitled 2.md" for a file in "Untitled 1")
                  // 2. Full paths including the folder (e.g., "Untitled 1/Untitled 2.md")
                  
                  // Check if the path already includes the folder prefix
                  const expectedPrefix = dirPath + '/'
                  const hasPrefix = file.path.startsWith(expectedPrefix)
                  
                  if (hasPrefix) {
                    // Path includes folder prefix, check if it's directly in the folder
                    const relativePath = file.path.substring(expectedPrefix.length)
                    if (relativePath.includes('/')) {
                      continue  // In a subfolder, skip it
                    }
                  } else {
                    // Path doesn't include folder prefix
                    // Check if the path contains any slashes (meaning it's in a subfolder)
                    if (file.path.includes('/')) {
                      continue  // File is in some subfolder
                    }
                    // File is directly in the queried folder
                  }
                } else {
                  // Root requested - only include root-level files
                  if (file.path.includes('/')) {
                    continue  // Skip files in subdirectories
                  }
                }
              }
              
              entries.push({
                name: file.name,
                path: file.path,
                type: file.type,
                size: file.size,
                modified: file.modified
              })
            }
            
            // console.log('Electron listFiles - entries:', entries)
          } else {
            // console.log('Electron listFiles - no files or error:', result.error)
          }
        } catch (err) {
          // console.error('Failed to list files via IPC:', err)
        }
      } else if (state.vaultHandle) {
        // Use browser FileSystem API
        try {
          let currentHandle = state.vaultHandle
          
          // Navigate to the specified directory if provided
          if (dirPath) {
            const parts = dirPath.split('/')
            for (const part of parts) {
              if (part) {
                currentHandle = await currentHandle.getDirectoryHandle(part)
              }
            }
          }
          
          // @ts-ignore
          for await (const entry of currentHandle.values()) {
            const fullPath = dirPath ? `${dirPath}/${entry.name}` : entry.name
            
            // Only include files and directories at the current level
            if (entry.kind === 'file') {
              let size: number | undefined
              let modified: number | undefined
              try {
                const fileData = await entry.getFile()
                size = fileData.size
                modified = fileData.lastModified
              } catch {
                // ignore
              }

              entries.push({
                name: entry.name,
                path: fullPath,
                type: 'file',
                size,
                modified
              })
            } else if (entry.kind === 'directory') {
              entries.push({
                name: entry.name,
                path: fullPath,
                type: 'directory'
              })
              
              // Only recurse if explicitly requested
              if (recursive && !entry.name.startsWith('.')) {
                const subEntries = await this.listFiles(fullPath, true)
                entries.push(...subEntries)
              }
            }
          }
        } catch (err) {
          console.error('Failed to list files:', err)
        }
      } else if (!state.vaultHandle && !isElectron) {
        throw new Error('No vault selected')
      }
      
      return entries
    },
    
    async deleteFile(path: string): Promise<void> {
      const state = get({ subscribe })
      
      // Check if we're in Electron and use IPC
      const isElectron = typeof window !== 'undefined' && (window as any).api
      
      if (isElectron) {
        // Use Electron IPC for file deletion
        try {
          const vaultPath = state.vaultPath || ''
          // If path is already absolute, use it as-is
          // If path starts with vault path, use it as-is
          // Otherwise, combine vault path with relative path
          let fullPath = path
          if (!path.startsWith('/') && !path.startsWith(vaultPath)) {
            fullPath = `${vaultPath}/${path}`
          }
          const result = await (window as any).api.deleteFile(fullPath)
          if (!result.success) {
            throw new Error(result.error || 'Failed to delete file')
          }
          return
        } catch (err) {
          console.error('Failed to delete file via IPC:', path, err)
          throw err
        }
      }
      
      if (!state.vaultHandle) throw new Error('No vault selected')
      
      try {
        await state.vaultHandle.removeEntry(path)
      } catch (err) {
        console.error('Failed to delete file:', path, err)
        throw err
      }
    },
    
    async renameFile(oldPath: string, newPath: string): Promise<void> {
      // Detect if this is a markdown file or another file type
      const isMarkdownFile = oldPath.endsWith('.md')
      const hasExtension = /\.(md|png|jpg|jpeg|gif|webp|svg|bmp|ico|txt)$/i.test(newPath)

      // Only add .md extension if:
      // 1. Old path was a markdown file
      // 2. New path doesn't already have an extension
      if (isMarkdownFile && !hasExtension) {
        newPath = `${newPath}.md`
      }

      // If paths are the same, no need to rename
      if (oldPath === newPath) {
        return
      }
      
      const state = get({ subscribe })
      
      // Check if we're in Electron and use IPC for atomic rename
      const isElectron = typeof window !== 'undefined' && (window as any).api?.renameFile
      
      if (isElectron) {
        // Use atomic rename via IPC for Electron
        try {
          const vaultPath = state.vaultPath || ''
          
          // Build full paths
          let fullOldPath = oldPath
          let fullNewPath = newPath
          if (!oldPath.startsWith('/') && !oldPath.startsWith(vaultPath)) {
            fullOldPath = `${vaultPath}/${oldPath}`
          }
          if (!newPath.startsWith('/') && !newPath.startsWith(vaultPath)) {
            fullNewPath = `${vaultPath}/${newPath}`
          }
          
          // Attempting IPC rename
          
          // Use the atomic rename-file IPC call
          const result = await (window as any).api.renameFile(fullOldPath, fullNewPath)
          
          // IPC rename result received
          
          if (result.success) {
            // Rename successful
            return
          } else {
            // If error is file not found, it might already be renamed
            if (result.error?.includes('not found')) {
              // File not found, might already be renamed
              return
            }
            // Rename failed
            throw new Error(result.error || 'Failed to rename file')
          }
        } catch (err: any) {
          // Exception during rename
          // Don't throw if file might already be renamed
          if (!err.message?.includes('not found')) {
            throw err
          }
        }
      } else {
        // Fallback to browser-based rename (copy + delete)
        if (!state.vaultHandle) throw new Error('No vault selected')

        try {
          // Assume old file exists (caller should verify)
          // Try to read the old file
          let actualOldPath = oldPath
          let content: string

          try {
            // Try to read the file directly
            content = await this.readFile(oldPath)
          } catch (err) {
            // If file not found, try without .md extension or with it
            if (oldPath.endsWith('.md')) {
              // Try without .md
              try {
                content = await this.readFile(oldPath.replace('.md', ''))
                actualOldPath = oldPath.replace('.md', '')
              } catch {
                console.error('Failed to rename file: old file not found')
                return
              }
            } else {
              // Try with .md
              try {
                content = await this.readFile(oldPath + '.md')
                actualOldPath = oldPath + '.md'
              } catch {
                console.error('Failed to rename file: old file not found')
                return
              }
            }
          }
          
          // Write to new location
          await this.writeFile(newPath, content)
          
          // Delete old file only if it's different from new path
          if (actualOldPath !== newPath) {
            await this.deleteFile(actualOldPath)
          }
        } catch (err) {
          // Failed to rename file in browser mode
          throw err
        }
      }
    },
    
    async renameFolder(oldPath: string, newPath: string): Promise<void> {
      const state = get({ subscribe })
      
      // Check if we're in Electron and use IPC
      const isElectron = typeof window !== 'undefined' && (window as any).api
      
      if (isElectron) {
        // For Electron, we need to implement this via IPC
        try {
          const vaultPath = state.vaultPath || ''
          
          // Build full paths
          let fullOldPath = oldPath
          let fullNewPath = newPath
          if (!oldPath.startsWith('/') && !oldPath.startsWith(vaultPath)) {
            fullOldPath = `${vaultPath}/${oldPath}`
          }
          if (!newPath.startsWith('/') && !newPath.startsWith(vaultPath)) {
            fullNewPath = `${vaultPath}/${newPath}`
          }
          
          // Use the rename-folder IPC call if available, otherwise use file operations
          const result = await (window as any).api.renameFolder(fullOldPath, fullNewPath)
          
          if (!result.success) {
            throw new Error(result.error || 'Failed to rename folder')
          }
          return
        } catch (err) {
          // Failed to rename folder via IPC, fall back to manual recursive move
          // Fall back to manual recursive move
        }
      }
      
      if (!state.vaultHandle) throw new Error('No vault selected')
      
      // For web, we need to:
      // 1. Create the new folder
      // 2. Recursively move all contents
      // 3. Delete the old folder
      
      try {
        // Create the new folder
        await this.createFolder(newPath)
        
        // Get list of all files and subfolders in the old folder
        const contents = await this.listFiles(oldPath)
        
        // Move each item
        for (const item of contents) {
          const oldItemPath = `${oldPath}/${item.name}`
          const newItemPath = `${newPath}/${item.name}`
          
          if (item.type === 'file') {
            // Move file
            await this.renameFile(oldItemPath, newItemPath)
          } else if (item.type === 'directory') {
            // Recursively move subfolder
            await this.renameFolder(oldItemPath, newItemPath)
          }
        }
        
        // Delete the old folder (now empty)
        await this.deleteFolder(oldPath)
      } catch (err) {
        // Failed to rename folder
        throw err
      }
    },
    
    async deleteFolder(folderPath: string): Promise<void> {
      const state = get({ subscribe })
      
      // Check if we're in Electron and use IPC
      const isElectron = typeof window !== 'undefined' && (window as any).api
      
      if (isElectron) {
        // Use Electron IPC for folder deletion
        try {
          const vaultPath = state.vaultPath || ''
          // If path is already absolute, use it as-is
          // If path starts with vault path, use it as-is
          // Otherwise, combine vault path with relative path
          let fullPath = folderPath
          if (!folderPath.startsWith('/') && !folderPath.startsWith(vaultPath)) {
            fullPath = `${vaultPath}/${folderPath}`
          }
          
          // Use deleteFile API which already handles both files and folders
          const result = await (window as any).api.deleteFile(fullPath)
          if (!result.success) {
            throw new Error(result.error || 'Failed to delete folder')
          }
          return
        } catch (err) {
          console.error('Failed to delete folder via IPC:', folderPath, err)
          throw err
        }
      }
      
      if (!state.vaultHandle) throw new Error('No vault selected')
      
      // For web, we need to recursively delete all contents first
      // This is a limitation of the File System Access API
      try {
        // Get the folder handle
        const parts = folderPath.split('/')
        let currentHandle = state.vaultHandle
        
        for (let i = 0; i < parts.length - 1; i++) {
          currentHandle = await currentHandle.getDirectoryHandle(parts[i])
        }
        
        const folderName = parts[parts.length - 1]
        
        // Remove the folder (this will fail if not empty in web)
        await currentHandle.removeEntry(folderName, { recursive: true })
      } catch (err) {
        console.error('Failed to delete folder:', folderPath, err)
        throw err
      }
    },
    
    // Request permission with user gesture
    async requestPermission(): Promise<boolean> {
      const state = get({ subscribe })

      if (!state.vaultHandle) return false

      // Electron always has permission
      const isElectron = typeof window !== 'undefined' && window.electronAPI !== undefined
      if (isElectron) return true

      try {
        if ('requestPermission' in state.vaultHandle && typeof state.vaultHandle.requestPermission === 'function') {
          const permission = await state.vaultHandle.requestPermission({ mode: 'readwrite' })
          return permission === 'granted'
        }
        return false
      } catch (err) {
        console.error('Failed to request permission:', err)
        return false
      }
    },
    
    // Check current permission status
    async checkPermission(): Promise<'granted' | 'denied' | 'prompt'> {
      const state = get({ subscribe })
      if (!state.vaultHandle) return 'denied'
      
      // Electron always has permission
      const isElectron = typeof window !== 'undefined' && window.electronAPI !== undefined
      if (isElectron) return 'granted'
      
      try {
        if ('queryPermission' in state.vaultHandle && typeof state.vaultHandle.queryPermission === 'function') {
          return await state.vaultHandle.queryPermission({ mode: 'readwrite' })
        }
        return 'prompt'
      } catch (err) {
        console.error('Failed to check permission:', err)
        return 'denied'
      }
    },
    
    // Clear metadata cache (for Electron)
    async clearCache(forceRefresh: boolean = true): Promise<void> {
      const state = get({ subscribe })
      const isElectron = typeof window !== 'undefined' && window.api?.clearCache
      
      if (isElectron) {
        console.log('🧹 Clearing metadata cache...')
        const result = await window.api!.clearCache!(forceRefresh ? state.vaultPath : undefined)
        if (result.success) {
          console.log('✅ Cache cleared successfully')
        } else {
          console.error('❌ Failed to clear cache')
        }
      } else {
        // For browser, clear cache
        try {
          const { getUnifiedCache } = await import('../services/UnifiedCache')
          const cache = await getUnifiedCache()
          await cache.clear()
          console.log('✅ Cache cleared successfully')
        } catch (error) {
          console.error('❌ Failed to clear cache:', error)
        }
      }
      
      // Also clear Content Index cache
      try {
        await contentIndexStore.clearCache()
        console.log('✅ Content Index cache cleared')
      } catch (error) {
        console.error('❌ Failed to clear Content Index cache:', error)
      }
    },
    
    // Force full reindexing of vault
    async reindexVault(): Promise<number> {
      console.log('🔄 Starting full vault reindexing...')
      try {
        // Clear existing Content Index cache first
        await contentIndexStore.clearCache()
        console.log('✅ Old index cleared')
        
        // Re-initialize with current vault path
        const state = get({ subscribe })
        if (state.vaultPath) {
          await contentIndexStore.initialize(state.vaultPath)
          console.log('✅ Content Index re-initialized')
          
          // Perform full indexing
          await contentIndexStore.performFullIndexing()
          
          const indexState = contentIndexStore.getState()
          const totalDocs = indexState?.stats?.totalDocuments || 0
          console.log(`✅ Reindexing complete: ${totalDocs} documents indexed`)
          
          return totalDocs
        } else {
          throw new Error('No vault path available')
        }
      } catch (err) {
        console.error('❌ Failed to reindex vault:', err)
        throw err
      }
    }
  }
}

export const vaultStore = createVaultStore()

// Expose to console for debugging (development mode only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).vaultStore = vaultStore
  ;(window as any).clearCache = () => vaultStore.clearCache(true)
  ;(window as any).reindexVault = () => vaultStore.reindexVault()
}
